import { useState, Fragment, useRef } from 'react';
import { Flex, Box, Text, Heading, Button } from 'rebass/styled-components';
import { ICustomAssignedPresetPlan, IPresetPlan, IPresetPlanSummary } from '../../../graphql/types/queries/presetPlans';
import { PresetPlanAnswers } from './PresetPlanAnswers';
import { HoverableCard } from '../LastCourses';
import { gql, useMutation } from '@apollo/client';
import { IOkPayload } from '../../../graphql/types/mutations/generics';
import { toast } from 'react-toastify';
import { GET_CUSTOM_ASSIGNED_PRESET_PLANS } from '../../../graphql/queries';
import moment from 'moment';
import Modal from 'styled-react-modal';
import { SpecialModalBackground } from '../../../components/Commons';
import styled from 'styled-components';
import TextArea, { TextAreaNoForm } from '../../../components/TextArea';

const Wrapper = styled(Flex)`
  overflow: auto;
  width: 600px;
  padding-right: 20px;
  margin-right: 20px;
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #6c89b7;
    border-radius: 5px;
  }
  &::-webkit-scrollbar {
    width: 7px;
    border: 1px solid transparent;
  }
`;

const ASSIGN_CUSTOM_PLAN = gql`
  mutation assignCustomPresetPlan($patientId: String!, $programId: String!, $internalNote: String) {
    assignCustomPresetPlan(patientId:$patientId, programId:$programId, internalNote:$internalNote){
      ok
    }
  }
`
const UNASSIGN_CUSTOM_PLAN = gql`
  mutation unassignCustomPresetPlan($patientId: String!, $programId: String!) {
    unassignCustomPresetPlan(patientId:$patientId, programId:$programId){
      ok
    }
  }
`

interface IInput {
  patientId: string
  programId: string
  internalNote?: string
}

interface IAssignOutput {
  assignCustomPresetPlan: IOkPayload
}

interface IUnassignOutput {
  unassignCustomPresetPlan: IOkPayload
}


export const AssignCustomPresetPlans = ({ presetPlans, customAssigned, patientId }: { presetPlans: IPresetPlan[], customAssigned: ICustomAssignedPresetPlan[], patientId: string }) => {
  const [assignable, setAssignable] = useState(JSON.parse(JSON.stringify(presetPlans.filter(x => !!x.assignedOnly))))
  const [readyToAssign, setReadyToAssign] = useState<string | null>(null)
  const [customNote, setCustomNote] = useState<string>('')
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const [extendedId, setExtendedId] = useState<string | null>();
  const [assignCustomPlan, { loading: loadingAssign }] = useMutation<IAssignOutput, IInput>(ASSIGN_CUSTOM_PLAN, {
    refetchQueries: [{ query: GET_CUSTOM_ASSIGNED_PRESET_PLANS, variables: { patientId } }]
  })
  const [unassignCustomPlan, { loading: loadingUnassign }] = useMutation<IUnassignOutput, IInput>(UNASSIGN_CUSTOM_PLAN, {
    refetchQueries: [{ query: GET_CUSTOM_ASSIGNED_PRESET_PLANS, variables: { patientId } }]
  })
  const toastId = useRef<any>(null);

  const _assign = async (programId: string) => {

    toastId.current = toast("Assigning Program", { autoClose: false, isLoading: true, closeButton: false });

    const res = await assignCustomPlan({
      variables: {
        patientId,
        programId,
        internalNote: customNote
      }
    })

    if (res && res.data && res.data.assignCustomPresetPlan) {
      if (res.data.assignCustomPresetPlan.ok) {
        toast.update(toastId.current, {
          render: 'Program Assigned!',
          type: toast.TYPE.SUCCESS,
          isLoading: false,
          autoClose: 5000,
          closeButton: null // The closeButton defined on ToastContainer will be used
        });
        setReadyToAssign(null)
        setCustomNote('')
      } else {
        toast.update(toastId.current, {
          render: 'An error occured',
          type: toast.TYPE.ERROR,
          isLoading: false,
          autoClose: 5000,
          closeButton: null // The closeButton defined on ToastContainer will be used
        });
      }
    } else {
      toast.update(toastId.current, {
        render: 'An error occured',
        type: toast.TYPE.ERROR,
        isLoading: false,
        autoClose: 5000,
        closeButton: null // The closeButton defined on ToastContainer will be used
      });
    }
  }
  const _unassign = async (programId: string) => {

    toastId.current = toast("Unassigning Program", { autoClose: false, isLoading: true, closeButton: false });

    const res = await unassignCustomPlan({
      variables: {
        patientId,
        programId
      }
    })

    if (res && res.data && res.data.unassignCustomPresetPlan) {
      if (res.data.unassignCustomPresetPlan.ok) {
        toast.update(toastId.current, {
          render: 'Program Unassigned!',
          type: toast.TYPE.SUCCESS,
          isLoading: false,
          autoClose: 5000,
          closeButton: null // The closeButton defined on ToastContainer will be used
        });
      } else {
        toast.update(toastId.current, {
          render: 'An error occured',
          type: toast.TYPE.ERROR,
          isLoading: false,
          autoClose: 5000,
          closeButton: null // The closeButton defined on ToastContainer will be used
        });
      }
    } else {
      toast.update(toastId.current, {
        render: 'An error occured',
        type: toast.TYPE.ERROR,
        isLoading: false,
        autoClose: 5000,
        closeButton: null // The closeButton defined on ToastContainer will be used
      });
    }
  }

  return (
    <Flex flexDirection={'column'} width={'100%'}>
      <Flex alignItems={'center'} mb={4} ml={'12px'} mt={2}>
        <Heading>Assignable Preset Plans</Heading>
      </Flex>
      {assignable.map((x: IPresetPlan) => {
        let customAssignedEntry = customAssigned.find(xx => xx.programId === x.id)
        return (
          <Fragment key={x.id}>
            <HoverableCard
              key={x.id}
              mb={3}
              style={{
                border: '1px solid #E2E2E2',
                borderRadius: '20px',
                opacity: !hoveredRow || hoveredRow === `${x.id}` ? 1 : 0.7,
                boxShadow: !hoveredRow || hoveredRow === `${x.id}` ? '0px 4px 20px rgb(0 0 0 / 10%)' : '0px 4px 20px rgb(0 0 0 / 0%)',
                width: '100%'
              }}
              onClick={() => {
                if (!customAssignedEntry) {
                  if (!!extendedId && extendedId === x.id) {
                    setExtendedId(null);
                  }
                  return;
                }
                if (!!extendedId && extendedId === x.id) {
                  setExtendedId(null);
                  return;
                }
                setExtendedId(x.id);
              }}
              onMouseEnter={() => setHoveredRow(`${x.id}`)}
              onMouseLeave={() => setHoveredRow(null)}>
              <Flex style={{ width: '100%', padding: '12px 12px', minHeight: '70px' }} flexDirection={'column'}>
                <Flex flex={1} alignItems={'center'}>
                  <svg style={{ opacity: !!customAssignedEntry ? 1 : 0, transform: extendedId && extendedId === x.id ? 'rotate(90deg)' : '' }} width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.39995 20C8.99995 20 8.59995 19.8 8.39995 19.6C7.79995 19 7.79995 18 8.39995 17.4L13.8 12L8.39995 6.59995C7.79995 5.99995 7.79995 4.99995 8.39995 4.39995C8.99995 3.79995 9.99995 3.79995 10.6 4.39995L17.2 10.8C17.8 11.4 17.8 12.4 17.2 13L10.6 19.4C10.2 19.8 9.79995 20 9.39995 20Z" fill='rgba(52,79,122,1)' />
                  </svg>
                  <Box style={{ width: '100px', paddingLeft: '12px', alignSelf: 'center' }}>
                    <Text style={{ color: 'rgba(52,79,122,0.6)', fontSize: '12px' }}>{x.name}</Text>
                  </Box>
                  <Box style={{ width: '1px', background: '#E2E2E2' }}></Box>
                  <Box flex={1} style={{ paddingLeft: '12px', alignSelf: 'center' }}>
                    <Text style={{ fontSize: '12px' }}>{x.description}</Text>
                  </Box>
                  <Box style={{ width: '1px', background: '#E2E2E2' }} display={['none', 'initial', 'initial']}></Box>
                  {!!customAssignedEntry ? (
                    <Button disabled={!!loadingAssign || !!loadingUnassign} onClick={() => _unassign(x.id)} p={'4px 24px'} variant={'pink'}>unassign</Button>
                  ) : (
                    <Button disabled={!!loadingAssign || !!loadingUnassign} onClick={() => setReadyToAssign(x.id)} p={'4px 24px'} variant={'primary'}>assign</Button>)}
                </Flex>
                {!!customAssignedEntry &&
                  extendedId && extendedId === x.id && (
                    <Flex flexDirection={'column'} ml={4} mt={3}>
                      <Flex mb={2}>
                        <Text style={{ color: 'rgba(52,79,122,0.6)', fontSize: '12px' }}>Added to patient on:</Text>
                        <Text style={{ fontSize: '12px' }} ml={2}>{moment(customAssignedEntry.addedOn).format('MM/DD/YYYY hh:mmA')}</Text>
                      </Flex>
                      <Flex mb={2}>
                        <Text style={{ color: 'rgba(52,79,122,0.6)', fontSize: '12px' }}>Added to patient by:</Text>
                        <Text style={{ fontSize: '12px' }} ml={2}>{customAssignedEntry.addedBy}</Text>
                      </Flex>
                      <Flex>
                        <Text style={{ color: 'rgba(52,79,122,0.6)', fontSize: '12px' }}>Internal notes:</Text>
                        <Text style={{ fontSize: '12px', fontStyle: !customAssignedEntry.customNotes ? 'italic' : '' }} ml={2}>{customAssignedEntry.customNotes || 'No note added'}</Text>
                      </Flex>
                    </Flex>
                  )}
              </Flex>
            </HoverableCard>
          </Fragment>
        );
      })}
      {!!readyToAssign && (
        <Modal
          isOpen={true}
          onBackgroundClick={() => {
            setReadyToAssign(null);
            setCustomNote('')
          }}
          onEscapeKeydown={() => {
            setReadyToAssign(null);
            setCustomNote('')
          }}
        >
          <SpecialModalBackground
            onClick={() => {
              setReadyToAssign(null);
              setCustomNote('')
            }}
          >
            <Box
              style={{
                margin: '0 auto',
                alignSelf: 'center',
                borderRadius: '20px',
                position: 'relative',
                background: 'linear-gradient(180deg, #BBD4ED 0%, #FDF5F2 100%)',
                padding: '24px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                maxWidth: '95vw',
                minWidth: 'initial',
                maxHeight: '450px',
                minHeight: '450px',
                overflowY: 'hidden',
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <Wrapper flex={1} flexDirection={'column'} width={'800px'} style={{ overflowY: 'auto' }}>
                <Heading mb={4}>Assign Program To Patient</Heading>

                <Text mb={2}>Enter internal note here if needed:</Text>
                <TextAreaNoForm
                  autoFocus
                  style={{ backgroundColor: 'white', flex: 1, minWidth: '430px', minHeight: '200px' }}
                  placeholder='internal note'
                  onChange={(e: any) => setCustomNote(e.target.value)}
                  value={customNote}
                />

                <Flex mt={4} alignItems={'center'} justifyContent={'center'}>
                  <Button variant={'pink'} mr={4}>Cancel</Button>
                  <Button variant={'primary'} onClick={() => _assign(readyToAssign)}>Assign</Button>
                </Flex>
              </Wrapper>
            </Box>
          </SpecialModalBackground>
        </Modal>
      )}
    </Flex>
  );
};
